/**
 * Utility functions to send notifications via Courier API when status >= 400
 */

import { store } from '@/core/store/store'
import { reportarAdvertencia } from '@/core/telemetria/reportarError'

interface CourierErrorNotificationParams {
  userId: string
  errorMessage: string
  errorTitle?: string
  statusCode: number
}

/**
 * Transforms error codes in message to markdown bold code format
 * Detects patterns like COM_TKP_LIM-CUZ_PAX_EXT and wraps them as **`pattern`**
 * @param message - Original error message
 * @returns Message with formatted error codes
 */
const formatErrorCodesInMessage = (message: string): string => {
  // Regex pattern to match codes like: COM_TKP_LIM-CUZ_PAX_EXT
  // Safer pattern that avoids catastrophic backtracking
  // Matches: Letter + segments separated by _ or - with at least one _
  const codePattern = /\b([A-Z]+(?:_[A-Z0-9]+)+(?:-[A-Z0-9]+)*)\b/g

  return message.replaceAll(codePattern, '**`$1`**')
}

/**
 * Sends a notification via Courier API for error cases
 * @param params - Notification parameters object
 */
export const sendCourierErrorNotification = async (params: CourierErrorNotificationParams): Promise<void> => {
  const { userId, errorMessage, errorTitle = 'Error en la aplicación', statusCode } = params

  // Solo enviar notificaciones para errores >= 400
  if (statusCode < 400) {
    return
  }

  try {
    const notificationData = {
      userId,
      title: errorTitle,
      message: formatErrorCodesInMessage(errorMessage),
      data: {
        statusCode,
        timestamp: new Date().toISOString(),
      },
    }

    const response = await fetch('/api/notifications/send', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(notificationData),
    })

    if (!response.ok) {
      // No interfiere con el flujo principal, pero se registra: antes solo se veia en desarrollo
      // y en produccion un envio fallido no dejaba rastro en ningun lado.
      reportarAdvertencia('No se pudo enviar la notificación de Courier', {
        respuesta: await response.text(),
      })
    }
  } catch (error) {
    // Que falle una notificacion no debe romper el flujo, pero si tiene que verse.
    reportarAdvertencia('Error al enviar la notificación de Courier', { error })
  }
}

/**
 * Helper function to get current user ID from Redux store
 */
export const getCurrentUserId = (): string | null => {
  try {
    // Dynamic import to avoid SSR issues
    const state = store.getState()

    // Get nombreUsuario from UserReducer (which is the email/userId)
    const nombreUsuario = state.UserReducer?.nombreUsuario

    return nombreUsuario || null
  } catch {
    return null
  }
}
